import { TRACKER_COLORS as C, MONO, STATUS_BADGES } from "../../constants/trackerUi";

/**
 * The day clock for a stage. Shows how far into the standard ETA the work is,
 * with a marker for today, so "day 3 of 7" is legible at a glance rather than
 * something you have to compute from two dates.
 */
export function DayMeter({ stage, stages, compact = false }) {
  const elapsed = stage?.daysElapsed;
  const etaDays = stage?.etaDays;
  const remaining = stage?.daysRemaining;
  /**
   * The closing stage is a sign-off, not work with a deadline. Its ETA is
   * suggested as "today" simply because closing happens the day someone does
   * it — so "due today" would read as a deadline it can never miss. Say what's
   * actually wanted instead.
   */
  const isClosing = !!stages?.length && stage?.stageCode === stages[stages.length - 1]?.stageCode;
  // Past the committed date matters more than past the standard allowance — a
  // date is a promise to someone, the department default is only a guide.
  const over = remaining != null ? remaining < 0 : elapsed != null && etaDays != null && elapsed > etaDays;
  const started = !!stage?.startedAt;

  /**
   * How far through the stage we are.
   *
   * Prefer the department's standard ETA when it sets one. Some stages are
   * deliberately open-ended (Requirement, Completed) — but once someone has
   * committed to a date, elapsed-vs-that-span is just as real a measure, so
   * the bar tracks the ETA instead of sitting empty.
   */
  let pct = 0;
  if (started && elapsed != null) {
    if (etaDays) {
      pct = Math.min(100, Math.round((elapsed / etaDays) * 100));
    } else if (remaining != null) {
      // elapsed days so far vs. the whole run (elapsed + what's left).
      const span = elapsed + Math.max(remaining, 0);
      pct = span > 0 ? Math.min(100, Math.round((elapsed / span) * 100)) : 100;
    }
  }
  if (over) pct = 100; // past the date — the bar is full and red

  const fillColor = over
    ? `linear-gradient(90deg, ${C.warn}, #935e05)`
    : `linear-gradient(90deg, ${C.accent}, ${C.accentInk})`;

  // Same rule as the card: only show a status that adds something. Awaiting
  // accept and not-started are already implied by the row around it.
  const status = stage?.status;
  const badge =
    status && status !== "pending_accept" && status !== "not_started" ? STATUS_BADGES[status] : null;

  if (compact) {
    // Rail and days on one line: the rail takes the space it needs and the
    // days sit at its end, so the block reads as one unit rather than a bar
    // with a number floating off to the right.
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {stages?.length ? (
            <StageRail
              stages={stages}
              currentCode={stage?.stageCode}
              compact
              blocked={stage?.status === "blocked"}
              currentLabel={stage?.label}
              badge={badge}
            />
          ) : (
            <div style={{ position: "relative", height: 6, borderRadius: 4, background: C.sunken }}>
              <div
                style={{
                  position: "absolute",
                  inset: "0 auto 0 0",
                  height: "100%",
                  width: `${started ? pct : 0}%`,
                  borderRadius: 4,
                  background: started ? fillColor : C.upcoming,
                  transition: "width .9s cubic-bezier(.2,.7,.2,1)",
                }}
              />
            </div>
          )}
        </div>

        <span
          style={{
            fontSize: "0.68rem",
            fontFamily: MONO,
            fontVariantNumeric: "tabular-nums",
            fontWeight: 700,
            // Closing is an invitation, not a warning — amber would misread.
            color: isClosing ? C.accentInk : over ? C.danger : remaining === 0 ? C.warn : C.inkFaint,
            whiteSpace: "nowrap",
            flexShrink: 0,
            // Nudge down so it sits level with the rail, not the label above it.
            alignSelf: "flex-end",
            paddingBottom: 1,
          }}
        >
          {isClosing
            ? "ready to close"
            : remaining != null
            ? remaining < 0
              ? `${Math.abs(remaining)}d overdue`
              : remaining === 0
              ? "due today"
              : `${remaining}d left`
            : started
            ? `${elapsed} / ${etaDays ?? "–"}d`
            : etaDays
            ? `${etaDays}d ETA`
            : "not started"}
        </span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 7 }}>
        <div
          style={{
            fontFamily: MONO,
            fontVariantNumeric: "tabular-nums",
            fontSize: "1.34rem",
            fontWeight: 700,
            color: over ? C.warn : C.accent,
            letterSpacing: "-0.02em",
            display: "flex",
            alignItems: "baseline",
            gap: 6,
          }}
        >
          <span>{started ? elapsed : 0}</span>
          <span style={{ fontSize: "0.8rem", color: C.inkFaint, fontWeight: 400 }}>
            {/* With no standard ETA, the run to the committed date is the
                honest denominator — "2 of 3" rather than a bare "2". */}
            {etaDays
              ? `of ${etaDays} working days`
              : started && elapsed != null && remaining > 0
              ? `of ${elapsed + remaining} working days`
              : "working days"}
          </span>
        </div>
        {stage?.etaDate ? (
          <div style={{ fontSize: "0.72rem", color: C.inkFaint, fontFamily: MONO, textAlign: "right" }}>
            ETA
            <b style={{ display: "block", color: C.inkSoft, fontSize: "0.79rem" }}>
              {new Date(stage.etaDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
            </b>
            {/* Runway to that date — the number people actually plan against. */}
            {isClosing ? (
              <span style={{ display: "block", marginTop: 2, fontWeight: 700, color: C.accentInk }}>
                ready to close
              </span>
            ) : remaining != null ? (
              <span
                style={{
                  display: "block",
                  marginTop: 2,
                  fontWeight: 700,
                  color: remaining < 0 ? C.danger : remaining === 0 ? C.warn : C.inkFaint,
                }}
              >
                {remaining < 0
                  ? `${Math.abs(remaining)} working day${Math.abs(remaining) === 1 ? "" : "s"} overdue`
                  : remaining === 0
                  ? "due today"
                  : `${remaining} working day${remaining === 1 ? "" : "s"} left`}
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div style={{ position: "relative", height: 9, borderRadius: 5, background: C.sunken, marginBottom: 8 }}>
        <div
          style={{
            position: "absolute",
            inset: "0 auto 0 0",
            height: "100%",
            width: `${started ? pct : 0}%`,
            borderRadius: 5,
            background: started ? fillColor : C.upcoming,
            transition: "width 1.1s cubic-bezier(.2,.7,.2,1)",
          }}
        />
        {started && pct > 0 ? (
          <div
            style={{
              position: "absolute",
              top: -5,
              left: `${Math.min(pct, 98)}%`,
              width: 3,
              height: 19,
              background: C.ink,
              borderRadius: 2,
            }}
          />
        ) : null}
      </div>

      {over ? (
        <div style={{ fontSize: "0.72rem", color: C.warn, fontWeight: 700 }}>
          {elapsed - etaDays} day{elapsed - etaDays === 1 ? "" : "s"} over the {etaDays}d ETA
        </div>
      ) : null}
    </div>
  );
}

/**
 * Where this requirement sits in the whole flow, as a row of pips. Compact
 * enough to live inside the stage panel without repeating the full pipeline.
 */
export function StageRail({ stages, currentCode, compact = false, blocked = false, currentLabel, badge }) {
  const dot = compact ? 8 : 9;
  const nowColor = blocked ? C.warn : C.accent;
  const nowHalo = blocked ? C.warnSoft : C.accentSoft;

  // Where the live stage sits along the rail, as a percentage, so its name can
  // be centred over it rather than floating at the left.
  const idx = stages.findIndex((s) => s.stageCode === currentCode);
  const currentPct = idx >= 0 && stages.length > 1 ? (idx / (stages.length - 1)) * 100 : 0;

  return (
    <div>
      {/* The stage name rides above its own dot, so the label and the pulse
          read as one thing rather than two separate bits of the row. */}
      {compact && currentLabel ? (
        <div style={{ position: "relative", height: 17, marginBottom: 1 }}>
          <span
            style={{
              position: "absolute",
              left: `${currentPct}%`,
              transform:
                currentPct <= 6 ? "translateX(0)" : currentPct >= 94 ? "translateX(-100%)" : "translateX(-50%)",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              whiteSpace: "nowrap",
              fontSize: "0.71rem",
              fontWeight: 700,
              color: blocked ? C.warn : C.accentInk,
            }}
          >
            {currentLabel}
            {badge ? (
              <span
                style={{
                  fontSize: "0.6rem",
                  fontWeight: 800,
                  padding: "1px 7px",
                  borderRadius: 100,
                  background: badge.bg,
                  color: badge.color,
                }}
              >
                {badge.label}
              </span>
            ) : null}
          </span>
        </div>
      ) : null}

      <div style={{ display: "flex", alignItems: "center" }}>
        {stages.map((s, i) => {
          const isDone = s.status === "done";
          const isSkipped = s.status === "skipped";
          const isNow = s.stageCode === currentCode;
          return (
            <div key={s.stageCode} style={{ display: "flex", alignItems: "center", flex: i === stages.length - 1 ? 0 : 1 }}>
              <span
                title={
                  isSkipped
                    ? `${s.label} — skipped`
                    : `${s.label}${s.ownerName ? ` — ${s.ownerName}` : ""}`
                }
                style={{
                  width: isNow ? dot + 2 : dot,
                  height: isNow ? dot + 2 : dot,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: isDone ? C.done : isNow ? nowColor : isSkipped ? "transparent" : C.line,
                  border: isSkipped ? `2px solid ${C.line}` : "none",
                  boxSizing: "border-box",
                  // The live stage breathes, so you can find it at a glance in
                  // a list of rows without reading any labels.
                  animation: isNow ? "trackerStagePulse 2s ease-in-out infinite" : "none",
                  boxShadow: isNow ? `0 0 0 3px ${nowHalo}` : "none",
                }}
              />
              {i < stages.length - 1 ? (
                <span style={{ flex: 1, height: 2, background: isDone || isSkipped ? C.done : C.line }} />
              ) : null}
            </div>
          );
        })}
      </div>
      {/* Every stage is named in the panel; on a list row only the live one is,
          since seven labels in that width would be unreadable. */}
      {!compact ? (
        <div style={{ display: "flex", marginTop: 6 }}>
          {stages.map((s, i) => (
            <span
              key={s.stageCode}
              style={{
                flex: i === stages.length - 1 ? 0 : 1,
                fontSize: "0.61rem",
                whiteSpace: "nowrap",
                color: s.stageCode === currentCode ? C.accentInk : C.inkFaint,
                fontWeight: s.stageCode === currentCode ? 700 : 400,
              }}
            >
              {s.label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
